// nono-measurement.js: consent-gated third parties (CONTRACT §6, FRAMEWORK §12, Ferdig innhold 00 §08).
//   Analyse        -> Microsoft Clarity (consentv2); Google Analytics 4 analytics_storage (Consent Mode v2,
//                     advanced: gtag itself is loaded by snippets/x-ga4 with everything denied by default).
//   Markedsføring  -> _fbc from fbclid, nono_attr, Meta Pixel (init + PageView), visit-attribution beacon;
//                     GA4 ad_storage / ad_user_data / ad_personalization (never on the two campaign pages).
// Also the GA4 events that no single module owns: video_play / video_pause on our films (players in a
// <dialog>, not the silent loops) and job_apply_submit (Shopify's contact form reloads the page).
// Nothing runs before a stored/explicit choice (html[data-consent="set"]) and nothing at all when
// window.NONO.sensitive is true, except Clarity (owner 21.10.2026: every page, loaded at once). The other
// vendors load on idle, one per idle slice.
// Withdrawal stops further loading and tells Clarity/Meta to stop (consent revoke / erase), but an
// already-loaded vendor script cannot be unloaded from the page: the next page load follows the new
// choice completely.

const NONO = window.NONO || {};
const html = document.documentElement;
const DAY_S = 86400;
const ATTR_DAYS = 90;

const state = { clarity: false, pixel: null, beaconSent: false, attrWritten: false };

/** Current choice, or null while the visitor has not chosen. */
const current = () =>
  html.dataset.consent === 'set'
    ? { analytics: html.dataset.consentAnalytics === '1', marketing: html.dataset.consentMarketing === '1' }
    : null;

// ---- idle queue: one vendor per idle slice ------------------------------------------------------
const queue = [];
let scheduled = false;
const onIdle = (fn) =>
  typeof window.requestIdleCallback === 'function'
    ? window.requestIdleCallback(fn, { timeout: 4000 })
    : setTimeout(fn, 200);

function enqueue(task) {
  queue.push(task);
  if (!scheduled) {
    scheduled = true;
    onIdle(drain);
  }
}

function drain() {
  const task = queue.shift();
  try {
    if (task) task();
  } catch (err) {
    console.warn('[nono-measurement]', err);
  }
  if (queue.length) onIdle(drain);
  else scheduled = false;
}

// ---- helpers ------------------------------------------------------------------------------------
function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function writeCookie(name, value, days) {
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${days * DAY_S}; path=/; SameSite=Lax${secure}`;
}

function deleteCookie(name) {
  const parts = location.hostname.split('.');
  document.cookie = `${name}=; Max-Age=0; path=/`;
  for (let i = 0; i < parts.length - 1; i += 1) {
    document.cookie = `${name}=; Max-Age=0; path=/; domain=.${parts.slice(i).join('.')}`;
  }
}

function addScript(src) {
  return new Promise((resolve) => {
    const s = document.createElement('script');
    s.async = true;
    s.src = src;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
}

const pageUrl = () => location.href.split('#')[0];

function fbclidFromUrl() {
  try {
    const v = new URL(location.href).searchParams.get('fbclid');
    return v ? v.slice(0, 512) : null;
  } catch {
    return null;
  }
}

function hasAdParams() {
  try {
    const q = new URL(location.href).searchParams;
    return ['fbclid', 'utm_source', 'utm_campaign', 'utm_content', 'ad_id'].some((k) => q.get(k));
  } catch {
    return false;
  }
}

// ---- Analyse: Microsoft Clarity ---------------------------------------------------------------
const clarityConsent = (c) => ({
  ad_Storage: c.marketing ? 'granted' : 'denied',
  analytics_Storage: 'granted',
});

function loadClarity() {
  const c = current();
  const id = String(NONO.clarityId || '');
  // Owner 21.10.2026: Clarity runs on every page, the two campaign pages included, with no delay.
  if (state.clarity || !c || !c.analytics || !/^[a-z0-9]{4,20}$/i.test(id)) return;
  state.clarity = true;
  // Official Clarity stub: calls queue until the tag loads.
  window.clarity =
    window.clarity ||
    function clarity() {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };
  window.clarity('consentv2', clarityConsent(c));
  addScript(`https://www.clarity.ms/tag/${encodeURIComponent(id)}`);
}

function stopClarity() {
  if (state.clarity && typeof window.clarity === 'function') {
    window.clarity('consentv2', { ad_Storage: 'denied', analytics_Storage: 'denied' });
    window.clarity('consent', false); // erases Clarity cookies and ends the session
  }
  ['_clck', '_clsk'].forEach(deleteCookie);
  try {
    sessionStorage.removeItem('nono_bk_sid');
  } catch {
    /* storage blocked */
  }
}

// ---- Markedsføring: _fbc, nono_attr, Meta Pixel, visit-attribution ----------------------------
function writeClickIds() {
  const fbclid = fbclidFromUrl();
  if (fbclid) {
    const existing = readCookie('_fbc');
    if (!existing || !existing.endsWith(`.${fbclid}`)) writeCookie('_fbc', `fb.1.${Date.now()}.${fbclid}`, ATTR_DAYS);
  }
  // Last-touch ad landing for later bookings on a bare URL (format read by nono-booking.js).
  if (!state.attrWritten && hasAdParams()) {
    state.attrWritten = true;
    writeCookie('nono_attr', JSON.stringify({ landing: pageUrl().slice(0, 2048), fbclid, ts: Date.now() }), ATTR_DAYS);
  }
}

function loadPixel() {
  const c = current();
  const id = String(NONO.pixelId || '');
  if (state.pixel || !c || !c.marketing || NONO.sensitive || !/^\d{5,20}$/.test(id)) return;
  if (!window.fbq) {
    // Meta Pixel base code (stub + async fbevents.js).
    const n = function fbq() {
      if (n.callMethod) n.callMethod.apply(n, arguments);
      else n.queue.push(arguments);
    };
    window.fbq = n;
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
  }
  state.pixel = addScript('https://connect.facebook.net/en_US/fbevents.js');
  window.fbq('consent', 'grant');
  window.fbq('init', id);
  window.fbq('track', 'PageView');
}

async function sendBeacon() {
  const c = current();
  const endpoint = String(NONO.endpoint || '').replace(/\/+$/, '');
  if (state.beaconSent || !c || !c.marketing || NONO.sensitive || !/^https:\/\//.test(endpoint)) return;
  state.beaconSent = true;
  // Give the pixel a moment to set _fbp (its first-party browser id).
  if (state.pixel) await Promise.race([state.pixel, new Promise((r) => setTimeout(r, 3000))]);
  const now = current();
  if (!now || !now.marketing) return; // withdrawn meanwhile

  const fbclid = fbclidFromUrl();
  const fbp = readCookie('_fbp');
  const fbc = readCookie('_fbc');
  if (!fbclid && !fbp && !fbc) return; // the backend drops identifier-less visits anyway

  const category = document.querySelector('[data-page-category]');
  const page = NONO.page || {};
  // Schema: apps/api/src/server.ts POST /api/visit-attribution -> recordVisit (attribution/store.ts).
  const payload = {
    fbclid,
    fbp,
    fbc,
    eventSourceUrl: pageUrl(),
    landingPageHandle: page.handle || null,
    pageCategory: category ? category.getAttribute('data-page-category') : page.template || null,
    referrer: document.referrer || null,
    marketingConsent: true, // ignored by the backend today; sent so the server can enforce it (00 §08)
  };
  try {
    await fetch(`${endpoint}/api/visit-attribution`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit',
      mode: 'cors',
    });
  } catch {
    /* silent: measurement never affects the page */
  }
}

function stopMarketing() {
  if (typeof window.fbq === 'function') window.fbq('consent', 'revoke');
}

// ---- Google Analytics 4: consent updates (Consent Mode v2, advanced) ---------------------------
function gaConsent(c) {
  if (typeof window.gtag !== 'function' || !c) return;
  const ads = c.marketing && !NONO.sensitive ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    analytics_storage: c.analytics ? 'granted' : 'denied',
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  });
  if (!c.analytics) {
    // Withdrawn: gtag stops writing, but the cookies it already wrote stay until we remove them.
    document.cookie.split('; ').map((x) => x.split('=')[0]).filter((n) => n === '_ga' || n.startsWith('_ga_')).forEach(deleteCookie);
  }
}

// ---- Google Analytics 4: events no single module owns ------------------------------------------
const track = (name, params) => {
  if (typeof window.nonoTrack === 'function') window.nonoTrack(name, params);
};

// Films the visitor chose to watch play inside a <dialog> (member stories, film tiles); the silent loops do not.
function videoName(v) {
  const label = v.dataset.name || v.closest('dialog')?.getAttribute('aria-label') || '';
  return label.replace(/\s+/g, ' ').trim().slice(0, 100);
}
['play', 'pause'].forEach((type) => {
  document.addEventListener(
    type,
    (e) => {
      const v = e.target;
      if (!(v instanceof HTMLVideoElement) || !v.closest('dialog')) return;
      if (type === 'pause' && v.ended) return;
      track(`video_${type}`, { video_name: videoName(v), page_path: location.pathname });
    },
    true,
  );
});

// Booking confirmed (key event): nono-booking.js leaves a note on the server's 201; /pages/booket sends it once.
try {
  const note = JSON.parse(sessionStorage.getItem('nono_ga_booked') || 'null');
  const ref = new URLSearchParams(location.search).get('ref');
  if (note && ref && note.ref === ref) {
    sessionStorage.removeItem('nono_ga_booked');
    track('booking_complete', { source: note.source || '', value: 0, currency: 'NOK' });
  }
} catch {
  /* storage blocked or bad note: nothing to send */
}

// Job application (22 §04): Shopify's contact form reloads with the receipt; the position was kept on submit.
document.addEventListener('submit', (e) => {
  const form = e.target instanceof HTMLFormElement ? e.target : null;
  const pos = form && form.classList.contains('p-lsk__form') ? form.querySelector('select[name="contact[stilling]"], select[name="stilling"]') : null;
  if (!pos) return;
  try {
    sessionStorage.setItem('nono_job_pos', pos.value || '');
  } catch {
    /* storage blocked */
  }
});
if (document.querySelector('.p-lsk__success')) {
  let position = '';
  try {
    position = sessionStorage.getItem('nono_job_pos') || '';
    sessionStorage.removeItem('nono_job_pos');
  } catch {
    /* storage blocked */
  }
  track('job_apply_submit', { position });
}

// ---- apply the current choice -------------------------------------------------------------------
function apply(c) {
  if (!c) return;
  // Clarity first and right away (not on idle): the first seconds of a short ad visit are the ones we need.
  if (c.analytics) {
    if (state.clarity && typeof window.clarity === 'function') window.clarity('consentv2', clarityConsent(c));
    else loadClarity();
  } else stopClarity();

  if (NONO.sensitive) return; // Meta pixel and the visit beacon still never run on the two campaign pages
  if (c.marketing) {
    writeClickIds();
    if (state.pixel && typeof window.fbq === 'function') window.fbq('consent', 'grant');
    else enqueue(loadPixel);
    enqueue(sendBeacon);
  } else stopMarketing();
}

// GA4 follows every choice on every page (x-ga4 already applied the stored one before its config call).
document.addEventListener('nono:consent', () => gaConsent(current()));

apply(current());
document.addEventListener('nono:consent', () => apply(current()));
